import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import AppLayout from '../components/AppLayout';
import { getTaxSetting, updateTaxSetting } from '../api/settingsApi';
import { formatDateTimeDDMMYYYY } from '../utils/dateFormat';
import styles from './TaxSettingsPage.module.css';

const translations = {
  en: {
    pageTitle: 'Tax Settings',
    subtitle: 'Set the system-wide VAT / tax rate applied to invoices and receipts.',
    currentRate: 'Current tax rate',
    lastUpdatedBy: 'Last updated by',
    lastUpdatedAt: 'Last updated at',
    newRate: 'New tax rate (%)',
    newRatePlaceholder: 'e.g. 14',
    save: 'Save',
    loading: 'Loading...',
    saving: 'Saving...',
    never: 'Never',
    unknown: 'Unknown',
    successMessage: 'Tax rate updated successfully.',
    failedFetch: 'Failed to load current tax rate. Please try again.',
    failedUpdate: 'Failed to update tax rate. Please try again.',
    validationError: 'Tax rate must be a number between 0 and 100.',
    homepage: 'Homepage',
    close: 'Close',
    note: 'This rate is used automatically when printing receipts with tax applied. All future prints will reflect the new rate.',
  },
  ar: {
    pageTitle: 'إعدادات الضريبة',
    subtitle: 'حدد معدل ضريبة القيمة المضافة المطبق على الفواتير والإيصالات.',
    currentRate: 'معدل الضريبة الحالي',
    lastUpdatedBy: 'آخر تعديل بواسطة',
    lastUpdatedAt: 'تاريخ آخر تعديل',
    newRate: 'معدل الضريبة الجديد (%)',
    newRatePlaceholder: 'مثال: 14',
    save: 'حفظ',
    loading: 'جارٍ التحميل...',
    saving: 'جارٍ الحفظ...',
    never: 'لم يتم التعديل بعد',
    unknown: 'غير معروف',
    successMessage: 'تم تحديث معدل الضريبة بنجاح.',
    failedFetch: 'فشل تحميل معدل الضريبة الحالي. حاول مرة أخرى.',
    failedUpdate: 'فشل تحديث معدل الضريبة. حاول مرة أخرى.',
    validationError: 'يجب أن يكون معدل الضريبة رقمًا بين 0 و100.',
    homepage: 'الصفحة الرئيسية',
    close: 'إغلاق',
    note: 'يُطبَّق هذا المعدل تلقائيًا عند طباعة الإيصالات مع الضريبة. ستعكس جميع الطباعات المستقبلية المعدل الجديد.',
  },
};

const formatDate = (value) => formatDateTimeDDMMYYYY(value, null);

const TaxSettingsPage = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  const [taxRate, setTaxRate] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (!token) return { userType: null };
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return { userType: payload.userType };
    } catch {
      return { userType: null };
    }
  }, [token]);

  const canEdit = ['admin', 'manager'].includes(userInfo.userType);

  useEffect(() => {
    const fetchTax = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const response = await getTaxSetting();
        const { taxRatePercent, updatedBy: by, updatedAt: at } = response.data;
        setTaxRate(taxRatePercent);
        setDraft(String(taxRatePercent));
        setUpdatedBy(by);
        setUpdatedAt(at);
      } catch {
        setErrorMessage(t('failedFetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchTax();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);

    const numeric = Number(draft);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
      setErrorMessage(t('validationError'));
      return;
    }

    setSaving(true);
    try {
      const response = await updateTaxSetting(numeric);
      const { taxRatePercent, updatedBy: by, updatedAt: at } = response.data;
      setTaxRate(taxRatePercent);
      setDraft(String(taxRatePercent));
      setUpdatedBy(by);
      setUpdatedAt(at);
      setSuccessMessage(t('successMessage'));
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('failedUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const formattedDate = formatDate(updatedAt);

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('subtitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading || saving }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner} />
          <p>{t('loading')}</p>
        </div>
      )}

      {!loading && (
        <div className={styles.card}>
          {successMessage && (
            <div className={`${styles.alert} ${styles.alertSuccess}`} role="status">
              <span>{successMessage}</span>
              <button type="button" onClick={() => setSuccessMessage(null)}>{t('close')}</button>
            </div>
          )}
          {errorMessage && (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              <span>{errorMessage}</span>
              <button type="button" onClick={() => setErrorMessage(null)}>{t('close')}</button>
            </div>
          )}

          <div className={styles.currentSection}>
            <div className={styles.rateDisplay}>
              <span className={styles.rateLabel}>{t('currentRate')}</span>
              <span className={styles.rateBadge}>
                {taxRate !== null ? `${taxRate}%` : '—'}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaItem}>
                <span className={styles.metaKey}>{t('lastUpdatedBy')}:</span>{' '}
                {updatedBy || t('unknown')}
              </span>
              <span className={styles.metaItem}>
                <span className={styles.metaKey}>{t('lastUpdatedAt')}:</span>{' '}
                {formattedDate || t('never')}
              </span>
            </div>
          </div>

          <p className={styles.note}>{t('note')}</p>

          {canEdit && (
            <div className={styles.editSection}>
              <label className={styles.label} htmlFor="tax-rate-input">
                {t('newRate')}
              </label>
              <div className={styles.inputRow}>
                <input
                  id="tax-rate-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className={styles.input}
                  placeholder={t('newRatePlaceholder')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={saving}
                />
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? t('saving') : t('save')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
};

export default TaxSettingsPage;
